/**
 * Local documents store.
 *
 * Offline-created documents live entirely in the browser under a single
 * localStorage key. They have no server counterpart until the user explicitly
 * syncs them, so they are kept separate from `offlineMilepadData` (which is the
 * offline *cache of server documents*, with conflict-resolution semantics).
 *
 * Every read/write of the raw key goes through this module so no other file has
 * to know the storage shape.
 */

const LOCAL_DOCUMENT_KEY = 'local_document_pad';

/** Maximum number of local documents a user may keep at once. */
export const MAX_LOCAL_DOCUMENTS = 5;

const EMPTY_CONTENT = () => ({ milepadData: '', summary: '', Signatures: [] });

// Read the whole map defensively — corrupt/absent storage falls back to {}.
function readStore() {
  try {
    const raw = localStorage.getItem(LOCAL_DOCUMENT_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.error('Error reading local documents:', error);
    return {};
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(LOCAL_DOCUMENT_KEY, JSON.stringify(store));
  } catch (error) {
    console.error('Error writing local documents:', error);
  }
}

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** All local documents, newest `updateDate` first. */
export function getLocalDocuments() {
  const store = readStore();
  return Object.values(store).sort(
    (a, b) => new Date(b?.updateDate || 0) - new Date(a?.updateDate || 0)
  );
}

/** A single local document record, or null. */
export function getLocalDocument(id) {
  if (!id) return null;
  return readStore()[id] || null;
}

/** How many local documents are currently stored. */
export function getLocalDocumentCount() {
  return Object.keys(readStore()).length;
}

/** True when the store is already at MAX_LOCAL_DOCUMENTS. */
export function isLocalDocumentLimitReached() {
  return getLocalDocumentCount() >= MAX_LOCAL_DOCUMENTS;
}

/**
 * Create a new local document with a fresh UUID and empty content.
 * Returns the created record, or null if the limit is already reached
 * (callers should check `isLocalDocumentLimitReached()` first and warn).
 */
export function createLocalDocument({ name, description = '', creator = null } = {}) {
  const store = readStore();
  if (Object.keys(store).length >= MAX_LOCAL_DOCUMENTS) {
    return null;
  }
  const now = new Date().toISOString();
  const record = {
    id: generateId(),
    name: (name || '').trim() || 'Untitled document',
    description: description || '',
    documentType: 6,
    isLocal: true,
    creator: creator || null,
    createDate: now,
    updateDate: now,
    content: EMPTY_CONTENT(),
  };
  store[record.id] = record;
  writeStore(store);
  return record;
}

/** Replace a local document's content and bump its updateDate. */
export function updateLocalDocumentContent(id, content) {
  const store = readStore();
  const record = store[id];
  if (!record) return null;
  record.content = {
    milepadData: content?.milepadData ?? record.content?.milepadData ?? '',
    summary: content?.summary ?? record.content?.summary ?? '',
    Signatures: content?.Signatures ?? record.content?.Signatures ?? [],
  };
  record.updateDate = new Date().toISOString();
  store[id] = record;
  writeStore(store);
  return record;
}

/** Rename a local document and bump its updateDate. */
export function renameLocalDocument(id, name) {
  const store = readStore();
  const record = store[id];
  if (!record) return null;
  record.name = (name || '').trim() || record.name;
  record.updateDate = new Date().toISOString();
  store[id] = record;
  writeStore(store);
  return record;
}

/** Permanently remove a local document from storage. */
export function removeLocalDocument(id) {
  const store = readStore();
  if (store[id]) {
    delete store[id];
    writeStore(store);
  }
}
